'use client';

import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Upload, Download, Copy, Trash2, ArrowRight, ArrowDown, Minus } from 'lucide-react';
import { saveAs } from 'file-saver';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { xmlMinifyContent } from '../../data/xml-minify-content';
import Link from 'next/link';

interface CodeEditorProps {
    value: string;
    onChange?: (value: string) => void;
    readOnly?: boolean;
    placeholder?: string;
    className?: string;
}

const CodeEditor = ({ value, onChange, readOnly, placeholder, className }: CodeEditorProps) => {
    return (
        <div className={cn(
            "relative flex h-full w-full overflow-hidden rounded-md border bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
            className
        )}>
            <Textarea
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                readOnly={readOnly}
                className="h-full w-full resize-none border-0 focus-visible:ring-0 rounded-none p-4 font-mono text-sm leading-6"
                placeholder={placeholder}
                spellCheck={false}
            />
        </div>
    );
};

const minifyXml = (xml: string, preserveComments: boolean = false): { minified: string; error?: string } => {
    try {
        if (!xml.trim()) {
            return { minified: '', error: 'Empty XML content' };
        }

        // Check if it's a complete XML document
        if (!xml.trim().startsWith('<')) {
            return { minified: xml, error: 'Invalid XML format: Must start with <' };
        }

        // First, handle CDATA sections by replacing them with placeholders
        const cdataSections: string[] = [];
        let processed = xml.replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, (match, content) => {
            cdataSections.push(content);
            return `__CDATA_${cdataSections.length - 1}__`;
        });

        // Remove XML comments if not preserving them
        if (!preserveComments) {
            processed = processed.replace(/<!--[\s\S]*?-->/g, '');
        }

        // Remove XML declaration and processing instructions
        processed = processed.replace(/<\?[^>]+\?>/g, '');

        // Remove all whitespace between tags
        processed = processed.replace(/>\s+</g, '><');

        // Remove leading/trailing whitespace
        processed = processed.trim();

        // Restore CDATA sections
        cdataSections.forEach((cdata, index) => {
            processed = processed.replace(`__CDATA_${index}__`, `<![CDATA[${cdata}]]>`);
        });

        return { minified: processed };
    } catch (error) {
        console.error('Error minifying XML:', error);
        return {
            minified: xml,
            error: error instanceof Error ? error.message : 'An error occurred while minifying XML'
        };
    }
};

const validateXml = (xml: string): { valid: boolean; error?: string } => {
    try {
        // Check for basic XML structure
        if (!xml.trim().startsWith('<')) {
            return { valid: false, error: 'Invalid XML: Must start with <' };
        }

        // Check for matching tags using a simple parser
        const tagStack: string[] = [];
        const tagRegex = /<([^>\s]+)[^>]*>/g;
        let match;
        let lastIndex = 0;

        while ((match = tagRegex.exec(xml)) !== null) {
            const [fullMatch, tagName] = match;
            const isClosing = fullMatch.startsWith('</');
            const isSelfClosing = fullMatch.endsWith('/>');
            const tag = tagName.split(' ')[0]; // Get just the tag name without attributes

            // Skip XML declaration, comments, and other special tags
            if (tag.startsWith('!') || tag.startsWith('?')) {
                continue;
            }

            if (isClosing) {
                if (tagStack.length === 0 || tagStack[tagStack.length - 1] !== tag) {
                    return {
                        valid: false,
                        error: `Mismatched tags: Expected </${tagStack[tagStack.length - 1]}>, found </${tag}>`
                    };
                }
                tagStack.pop();
            } else if (!isSelfClosing) {
                // Only push to stack if it's not a self-closing tag
                tagStack.push(tag);
            }

            lastIndex = tagRegex.lastIndex;
        }

        // Check for unclosed tags
        if (tagStack.length > 0) {
            return {
                valid: false,
                error: `Unclosed tag: <${tagStack[tagStack.length - 1]}>`
            };
        }

        return { valid: true };
    } catch (error) {
        return {
            valid: false,
            error: error instanceof Error ? error.message : 'An error occurred while validating XML'
        };
    }
};

export default function XmlMinifier() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [preserveComments, setPreserveComments] = useState(false);
    const [stats, setStats] = useState<{ originalSize: string; minifiedSize: string; saved: string; percentage: number } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();

    const minifyXmlHandler = () => {
        try {
            const inputValue = input.trim();

            if (!inputValue) {
                toast({
                    title: "Error",
                    description: "Please enter some XML to minify",
                    variant: "destructive",
                });
                return;
            }

            // First, validate the XML
            const validation = validateXml(inputValue);
            if (!validation.valid) {
                toast({
                    title: "XML Validation Error",
                    description: validation.error || "Invalid XML format",
                    variant: "destructive",
                });
                return;
            }

            // Then minify the XML
            const { minified, error } = minifyXml(inputValue, preserveComments);

            if (error) {
                throw new Error(error);
            }

            // Update the output state
            setOutput(minified);

            // Calculate and set stats
            if (minified) {
                const originalSize = new TextEncoder().encode(inputValue).length;
                const minifiedSize = new TextEncoder().encode(minified).length;
                const saved = originalSize - minifiedSize;
                const percentage = Math.round((saved / originalSize) * 100);

                setStats({
                    originalSize: formatFileSize(originalSize),
                    minifiedSize: formatFileSize(minifiedSize),
                    saved: formatFileSize(saved),
                    percentage
                });
            }

            toast({
                title: "Success",
                description: "XML minified successfully!",
            });
        } catch (error) {
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "An error occurred while minifying XML",
                variant: "destructive",
            });
            console.error('Error minifying XML:', error);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
            toast({
                title: "File Uploaded",
                description: `Loaded ${file.name}`,
            });
        };
        reader.readAsText(file);
    };

    const handleDownload = () => {
        if (!output) return;
        const blob = new Blob([output], { type: "application/xml;charset=utf-8" });
        saveAs(blob, "minified.xml");
        toast({
            title: "Downloaded",
            description: "File saved as minified.xml",
        });
    };

    const copyToClipboard = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        toast({
            title: "Copied!",
            description: "Minified XML copied to clipboard",
        });
    };

    const clearAll = () => {
        setInput('');
        setOutput('');
        setStats(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const sampleXml = `<?xml version="1.0" encoding="UTF-8"?>
<catalog>
  <!-- This is a sample XML catalog -->
  <book id="bk101">
    <author>Gambardella, Matthew</author>
    <title>XML Developer's Guide</title>
    <genre>Computer</genre>
    <price>44.95</price>
    <publish_date>2000-10-01</publish_date>
    <description>An in-depth look at creating applications with XML.</description>
  </book>
  <book id="bk102">
    <author>Ralls, Kim</author>
    <title>Midnight Rain</title>
    <genre>Fantasy</genre>
    <price>5.95</price>
    <publish_date>2000-12-16</publish_date>
    <description>A former architect battles corporate zombies, an evil sorceress, and her own childhood to become queen of the world.</description>
  </book>
  <book id="bk103">
    <author>Corets, Eva</author>
    <title>Maeve Ascendant</title>
    <genre>Fantasy</genre>
    <price>5.95</price>
    <publish_date>2000-11-17</publish_date>
    <description>After the collapse of a nanotechnology society in England, the young survivors lay the foundation for a new society.</description>
  </book>
</catalog>`;

    const loadSample = () => {
        setInput(sampleXml);
    };

    return (
        <div className="container mx-auto p-4 max-w-[1400px]">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <ToolPageIntro title="XML Minifier">
                Minify XML code to reduce file size and improve transmission speed. Our free XML minifier removes whitespace and formatting while preserving functionality.
            </ToolPageIntro>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={loadSample}>
                        Load Sample
                    </Button>
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                        <Upload className="mr-2 h-4 w-4" /> Upload File
                    </Button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept=".xml"
                        onChange={handleFileUpload}
                    />
                </div>
            </div>

            <div className="mb-6 p-4 border rounded-lg bg-muted/30">
                <h3 className="font-medium mb-3">Minification Options</h3>
                <div className="flex items-center justify-between">
                    <label htmlFor="preserve-comments" className="text-sm cursor-pointer">
                        <div className="font-medium">Preserve Comments</div>
                        <div className="text-muted-foreground text-xs">Keep XML comments in the minified output</div>
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            id="preserve-comments"
                            checked={preserveComments}
                            onChange={(e) => setPreserveComments(e.target.checked)}
                            className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 items-stretch">
                {/* Input Section */}
                <Card className="flex-1 flex flex-col min-h-[500px] lg:h-[700px]">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg font-medium flex justify-between items-center">
                            <span>Input XML</span>
                            <Button variant="ghost" size="sm" onClick={clearAll} className="text-muted-foreground hover:text-destructive">
                                <Trash2 className="h-4 w-4 mr-1" /> Clear
                            </Button>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 relative overflow-hidden">
                        <CodeEditor
                            value={input}
                            onChange={setInput}
                            placeholder="Paste your XML here, upload a file, or try the sample..."
                            className="border-0 rounded-none"
                        />
                    </CardContent>
                </Card>

                {/* Action Section */}
                <div className="flex lg:flex-col justify-center items-center gap-4 py-4 lg:py-0">
                    <Button
                        onClick={minifyXmlHandler}
                        size="lg"
                        className="rounded-full w-16 h-16 lg:w-20 lg:h-20 p-0 shadow-lg hover:scale-105 transition-transform"
                        title="Minify XML"
                    >
                        <ArrowRight className="h-8 w-8 hidden lg:block" />
                        <ArrowDown className="h-8 w-8 lg:hidden" />
                    </Button>
                </div>

                {/* Output Section */}
                <Card className="flex-1 flex flex-col min-h-[500px] lg:h-[700px]">
                    <CardHeader className="pb-3 border-b bg-muted/20">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <CardTitle className="text-lg font-medium">Minified Output</CardTitle>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="icon" onClick={copyToClipboard} disabled={!output} title="Copy">
                                    <Copy className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="icon" onClick={handleDownload} disabled={!output} title="Download">
                                    <Download className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 bg-muted/30 relative overflow-hidden">
                        <CodeEditor
                            value={output}
                            readOnly
                            placeholder="Minified XML will appear here..."
                            className="border-0 rounded-none bg-transparent"
                        />
                    </CardContent>

                    {/* Stats */}
                    {stats && (
                        <div className="bg-background/90 border-t p-3 text-xs text-muted-foreground">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <div>
                                    <span className="block text-muted-foreground">Original:</span>
                                    <span className="font-medium text-foreground">{stats.originalSize}</span>
                                </div>
                                <div>
                                    <span className="block text-muted-foreground">Minified:</span>
                                    <span className="font-medium text-foreground">{stats.minifiedSize}</span>
                                </div>
                                <div>
                                    <span className="block text-muted-foreground">Saved:</span>
                                    <span className="font-medium text-green-600 dark:text-green-400">{stats.saved}</span>
                                </div>
                                <div>
                                    <span className="block text-muted-foreground">Reduction:</span>
                                    <span className="font-medium text-green-600 dark:text-green-400">{stats.percentage}%</span>
                                </div>
                            </div>
                        </div>
                    )}
                </Card>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={xmlMinifyContent.title}
                description={xmlMinifyContent.description}
                content={xmlMinifyContent.content}
                faqs={xmlMinifyContent.faqs}
                relatedTools={xmlMinifyContent.relatedTools}
            />

            <Toaster />
        </div>
    );
}
